import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { trackConversionFromHref } from '@/lib/track-site-event';
import { htmlSeguro } from '../utils/htmlSeguro';

interface ImageTextProps {
  image?: string;
  imageAlt?: string;
  imagePosition?: 'left' | 'right';
  imageWidth?: number;
  imageRadius?: number;
  title?: string;
  text?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  titleColor?: string;
  textColor?: string;
  bgColor?: string;
  gap?: number;
  padding?: number;
  borderRadius?: number;
  verticalAlign?: 'start' | 'center' | 'end';
}

const ALINEAR_VERTICAL: Record<string, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/**
 * Bloque imagen + texto — render del sitio público.
 *
 * El bloque más usado de cualquier builder: una imagen a un lado, y título,
 * texto y un botón opcional al otro. En móvil siempre se apila con la imagen
 * arriba; el ancho de la imagen solo manda de `md` para arriba, que es donde hay
 * dos columnas. Ese ancho viaja por variable CSS (`--ancho-img`) porque Tailwind
 * purga por coincidencia de texto y no se pueden armar clases interpolando.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/ImageText.tsx.
 */
export const ImageText = ({
  image = '',
  imageAlt = '',
  imagePosition = 'left',
  imageWidth = 50,
  imageRadius = 12,
  title = '',
  text = '',
  buttonText = '',
  buttonLink = '#',
  buttonBgColor = '#1e88e5',
  buttonTextColor = '#ffffff',
  titleColor = '#111827',
  textColor = '#4b5563',
  bgColor = 'transparent',
  gap = 32,
  padding = 24,
  borderRadius = 0,
  verticalAlign = 'center',
}: ImageTextProps) => {
  const { connectors } = useNode();

  const anchoImagen = Math.min(75, Math.max(25, Number(imageWidth) || 50));

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      className={`flex w-full flex-col ${imagePosition === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      style={{
        ['--ancho-img' as string]: `${anchoImagen}%`,
        background: bgColor,
        padding: `${padding}px`,
        borderRadius: `${borderRadius}px`,
        gap: `${gap}px`,
        alignItems: ALINEAR_VERTICAL[verticalAlign] ?? 'center',
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      {!!image && (
        <div className="w-full shrink-0 md:w-[var(--ancho-img)]">
          <Img loading="eager" sizes="(max-width: 768px) 100vw, 50vw"
            src={image}
            alt={imageAlt}
            className="block w-full"
            style={{ borderRadius: `${imageRadius}px`, objectFit: 'cover' }}
          />
        </div>
      )}
      <div className="w-full min-w-0 md:flex-1">
        {!isBlankHtml(title) && (
          <h3
            className="mb-3 text-2xl font-bold md:text-3xl"
            style={{ color: titleColor }}
            dangerouslySetInnerHTML={htmlSeguro(title || '')}
          />
        )}
        {!isBlankHtml(text) && (
          <div
            className="text-base leading-relaxed"
            style={{ color: textColor }}
            dangerouslySetInnerHTML={htmlSeguro(text || '')}
          />
        )}
        {!isBlankHtml(buttonText) && (
          <a
            href={buttonLink || '#'}
            onClick={() => trackConversionFromHref(buttonLink || '')}
            className="mt-5 inline-block rounded-lg px-6 py-3 font-semibold transition-opacity hover:opacity-90"
            style={{ background: buttonBgColor, color: buttonTextColor }}
          >
            {/* El texto va en un <span> propio para que el editor —donde ese span
                es el campo editable— tenga exactamente la misma estructura. */}
            <span dangerouslySetInnerHTML={htmlSeguro(buttonText || '')} />
          </a>
        )}
      </div>
    </div>
  );
};

ImageText.craft = {
  displayName: 'ImageText',
  props: {
    image: '',
    imageAlt: '',
    imagePosition: 'left',
    imageWidth: 50,
    imageRadius: 12,
    title: 'Un título que acompaña la imagen',
    text: 'Contá acá lo que quieras destacar. Este bloque combina una imagen con texto y un botón opcional.',
    buttonText: '',
    buttonLink: '#',
    buttonBgColor: '#1e88e5',
    buttonTextColor: '#ffffff',
    titleColor: '#111827',
    textColor: '#4b5563',
    bgColor: 'transparent',
    gap: 32,
    padding: 24,
    borderRadius: 0,
    verticalAlign: 'center',
  },
};
